import { useEffect, useId, useState, type ReactNode } from "react";
import { InfoTip } from "@client/components/InfoTip";
import { labelText, type HintPlacement } from "@client/lib/fieldHints";
import { currencySymbol } from "@shared/roi/format";
import { parseNumeric, sliderCeiling } from "@client/lib/numericInput";

/**
 * The editor's number inputs: `MoneyField`, `PriceField`, `CountField` and
 * `PercentField`, all over one tolerant text input.
 *
 * Why not `<input type="number">`: it discards a pasted "1,200" without saying
 * so, scrolls the value when the wheel passes over it, and renders a spinner
 * that is below the 24px target size on every browser. What the editor needs is
 * the numeric keypad on a phone and a value that survives being typed, which is
 * `inputMode="decimal"` over a text field.
 *
 * The clamping rule is from docs/08-frontend.md: **on blur, not while typing**.
 * Clamping mid-keystroke means the "1" of a "150" you are typing into a 0–100
 * field becomes the whole value and the rest overwrites it; you end up with 0.
 * So the parsed value is published live — the charts move with the digits — and
 * only the settled value is pulled into range.
 */

/* ------------------------------------------------------------------- input */

const toText = (value: number): string => (Number.isFinite(value) ? String(value) : "");

interface NumericInputProps {
  id: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  integer?: boolean;
  disabled?: boolean;
  describedBy?: string;
  className?: string;
}

function NumericInput({
  id,
  value,
  onChange,
  min,
  max,
  integer = false,
  disabled = false,
  describedBy,
  className = "",
}: NumericInputProps) {
  // While the field has focus the text the user typed is the truth; the rest of
  // the time the model is. Keeping both and choosing between them is what lets
  // "1." and "0.05" survive long enough to be finished.
  const [typed, setTyped] = useState("");
  const [focused, setFocused] = useState(false);

  const settle = (candidate: number): number => {
    const bounded = Math.min(max, Math.max(min, candidate));
    return integer ? Math.round(bounded) : bounded;
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      disabled={disabled}
      aria-describedby={describedBy}
      value={focused ? typed : toText(value)}
      onFocus={() => {
        setTyped(toText(value));
        setFocused(true);
      }}
      onChange={(event) => {
        const raw = event.target.value;
        setTyped(raw);
        const parsed = parseNumeric(raw);
        // An empty box is a 0 in the model but stays empty on screen, so nobody
        // has to delete a "0" before typing over it.
        onChange(parsed === null ? 0 : parsed);
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parseNumeric(typed);
        const next = settle(parsed ?? 0);
        if (next !== value) onChange(next);
      }}
      className={`tabular w-full rounded-md border border-line bg-chip-bg px-2 py-1.5 text-right text-sm text-ink disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
    />
  );
}

/* ------------------------------------------------------------------- shell */

export type FieldTone = "neutral" | "revenue" | "cost";

const TONE_COLOR: Record<FieldTone, string | undefined> = {
  neutral: undefined,
  revenue: "var(--roi-revenue)",
  cost: "var(--roi-cost)",
};

interface ShellProps {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  hintPlacement?: HintPlacement;
  tone: FieldTone;
  /** "$" or "%" — the unit, shown beside the box rather than inside the label. */
  affix?: string;
  affixSide?: "start" | "end";
  children: (describedBy: string | undefined) => ReactNode;
  /** The slider, when there is one, under the whole row. */
  below?: ReactNode;
}

/**
 * The hue key from the ZNMS page: a revenue field and the revenue bars it feeds
 * carry the same colour, so a number and its consequence read as one thing. The
 * colour is on a dot and the label, never the only signal — the label says
 * "price" or "cost" in words.
 */
function FieldShell({
  id,
  label,
  hint,
  hintPlacement = "tip",
  tone,
  affix,
  affixSide = "start",
  children,
  below,
}: ShellProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const color = TONE_COLOR[tone];

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="flex items-center gap-1.5 text-xs font-semibold text-ink">
          {color && (
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ background: color }}
            />
          )}
          {label}
        </label>
        {hint && hintPlacement === "tip" && (
          <InfoTip id={hintId} label={labelText(label)}>
            {hint}
          </InfoTip>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        {affix && affixSide === "start" && (
          <span aria-hidden="true" className="shrink-0 text-xs text-ink-soft">
            {affix}
          </span>
        )}
        {children(hintId)}
        {affix && affixSide === "end" && (
          <span aria-hidden="true" className="shrink-0 text-xs text-ink-soft">
            {affix}
          </span>
        )}
      </div>

      {below}

      {hint && hintPlacement === "below" && (
        <p id={hintId} className="text-xs leading-snug text-ink-soft">
          {hint}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ slider */

function Slider({
  label,
  value,
  ceiling,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  ceiling: number;
  onChange: (value: number) => void;
  disabled: boolean;
}) {
  return (
    <input
      type="range"
      // Named for a screen reader without repeating the visible label as a
      // second control with the same name — "Starter price" and "Drag Starter
      // price" are distinguishable in a list of form controls.
      aria-label={`Drag ${label}`}
      min={0}
      max={ceiling}
      step={ceiling / 100}
      value={Math.min(value, ceiling)}
      disabled={disabled}
      onChange={(event) => onChange(Number(event.target.value))}
      className="h-1.5 w-full cursor-pointer accent-[var(--azure-deep)] disabled:cursor-not-allowed disabled:opacity-50"
    />
  );
}

/* ------------------------------------------------------------------ fields */

interface MoneyFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  currencyCode: string;
  hint?: ReactNode;
  hintPlacement?: HintPlacement;
  tone?: FieldTone;
  disabled?: boolean;
  max?: number;
}

/** A figure in money. No slider — for the fields people type once and leave. */
export function MoneyField({
  label,
  value,
  onChange,
  hintPlacement,
  currencyCode,
  hint,
  tone = "neutral",
  disabled = false,
  max = 1_000_000_000,
}: MoneyFieldProps) {
  const id = useId();
  return (
    <FieldShell
      id={id}
      label={label}
      hint={hint}
      hintPlacement={hintPlacement}
      tone={tone}
      affix={currencySymbol(currencyCode)}
    >
      {(describedBy) => (
        <NumericInput
          id={id}
          value={value}
          onChange={onChange}
          min={0}
          max={max}
          disabled={disabled}
          describedBy={describedBy}
        />
      )}
    </FieldShell>
  );
}

/**
 * Money plus a slider — the pairing from the ZNMS page, for the handful of
 * figures a scenario is actually explored with. The slider is for "what if it
 * were half that"; the box is for the figure sales actually quoted.
 */
export function PriceField({
  label,
  value,
  onChange,
  hintPlacement,
  currencyCode,
  hint,
  tone = "neutral",
  disabled = false,
  max = 1_000_000_000,
  sliderFloor = 1000,
}: MoneyFieldProps & { sliderFloor?: number }) {
  const id = useId();
  // The ceiling grows to fit the value and never shrinks while the field is on
  // screen: a range that re-scaled under the thumb would make dragging useless.
  const [ceiling, setCeiling] = useState(() => sliderCeiling(value, sliderFloor));
  useEffect(() => {
    setCeiling((current) => (value > current ? sliderCeiling(value, sliderFloor) : current));
  }, [value, sliderFloor]);

  return (
    <FieldShell
      id={id}
      label={label}
      hint={hint}
      hintPlacement={hintPlacement}
      tone={tone}
      affix={currencySymbol(currencyCode)}
      below={
        <Slider
          label={label}
          value={value}
          ceiling={ceiling}
          disabled={disabled}
          onChange={(next) => onChange(Math.round(next))}
        />
      }
    >
      {(describedBy) => (
        <NumericInput
          id={id}
          value={value}
          onChange={onChange}
          min={0}
          max={max}
          disabled={disabled}
          describedBy={describedBy}
        />
      )}
    </FieldShell>
  );
}

/** A whole number of things: clients, seats, units. */
export function CountField({
  label,
  value,
  onChange,
  hintPlacement,
  hint,
  disabled = false,
  max = 1_000_000,
  min = 0,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  hint?: ReactNode;
  hintPlacement?: HintPlacement;
  disabled?: boolean;
  max?: number;
  min?: number;
}) {
  const id = useId();
  return (
    <FieldShell id={id} label={label} hint={hint} hintPlacement={hintPlacement} tone="neutral">
      {(describedBy) => (
        <NumericInput
          id={id}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          integer
          disabled={disabled}
          describedBy={describedBy}
        />
      )}
    </FieldShell>
  );
}

/** 0–100. The "%" sits after the box because that is where it is read. */
export function PercentField({
  label,
  value,
  onChange,
  hintPlacement,
  hint,
  tone = "neutral",
  disabled = false,
  max = 100,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  hint?: ReactNode;
  hintPlacement?: HintPlacement;
  tone?: FieldTone;
  disabled?: boolean;
  max?: number;
}) {
  const id = useId();
  return (
    <FieldShell
      id={id}
      label={label}
      hint={hint}
      hintPlacement={hintPlacement}
      tone={tone}
      affix="%"
      affixSide="end"
    >
      {(describedBy) => (
        <NumericInput
          id={id}
          value={value}
          onChange={onChange}
          min={0}
          max={max}
          disabled={disabled}
          describedBy={describedBy}
        />
      )}
    </FieldShell>
  );
}
